import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import type { AppPermissionKey } from "@/types/permissions";

const buildRedirect = (pathname: string, search: string, hash: string) =>
  `${pathname}${search}${hash}`;

const LoginRedirect = ({ target }: { target: string }) => (
  <Navigate to={`/login?redirect=${encodeURIComponent(target)}`} replace />
);

const InternalPolicyRedirect = ({ target }: { target: string }) => (
  <Navigate to={`/internal/gdpr-approval?redirect=${encodeURIComponent(target)}`} replace />
);

const LoadingState = () => (
  <div className="min-h-screen admin-shell flex items-center justify-center">
    <p className="text-sm text-muted-foreground">Betöltés...</p>
  </div>
);

interface RequirePermissionProps {
  permission: AppPermissionKey | string;
  fallbackPath?: string;
  children: ReactNode;
}

interface RequireAnyPermissionProps {
  permissions: Array<AppPermissionKey | string>;
  fallbackPath?: string;
  children: ReactNode;
}

export const RequirePermission = ({ permission, fallbackPath = "/", children }: RequirePermissionProps) => {
  const { user, loading, internalPolicyAccepted, internalPolicyLoading } = useAuth();
  const { loading: permissionsLoading, hasPermission } = usePermissions();
  const location = useLocation();

  if (loading || internalPolicyLoading || permissionsLoading) return <LoadingState />;
  if (!user) {
    const target = buildRedirect(location.pathname, location.search, location.hash);
    return <LoginRedirect target={target} />;
  }
  if (!internalPolicyAccepted) {
    const target = buildRedirect(location.pathname, location.search, location.hash);
    return <InternalPolicyRedirect target={target} />;
  }

  if (!hasPermission(permission)) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
};

export const RequireAnyPermission = ({ permissions, fallbackPath = "/", children }: RequireAnyPermissionProps) => {
  const { user, loading, internalPolicyAccepted, internalPolicyLoading } = useAuth();
  const { loading: permissionsLoading, hasAny } = usePermissions();
  const location = useLocation();

  if (loading || internalPolicyLoading || permissionsLoading) return <LoadingState />;
  if (!user) {
    const target = buildRedirect(location.pathname, location.search, location.hash);
    return <LoginRedirect target={target} />;
  }
  if (!internalPolicyAccepted) {
    const target = buildRedirect(location.pathname, location.search, location.hash);
    return <InternalPolicyRedirect target={target} />;
  }

  if (!hasAny(permissions)) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
};
